import type { RaceResult, UserStats } from '@typing-racer/shared';

export interface Session {
  token: string;
  name: string;
}
const KEY = 'typing-racer.session';

export function loadSession(): Session | null {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Session | null;
    return s && s.token && s.name ? s : null;
  } catch {
    return null;
  }
}
function saveSession(s: Session | null): void {
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s));
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

async function api<T>(path: string, init: { method?: string; body?: unknown; token?: string | null } = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (init.token) headers['authorization'] = `Bearer ${init.token}`;
  const res = await fetch(path, { method: init.method ?? 'GET', headers, body: init.body === undefined ? null : JSON.stringify(init.body) });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`);
  return data;
}

export class Account {
  session: Session | null = loadSession();

  get name(): string | null {
    return this.session?.name ?? null;
  }
  get token(): string | null {
    return this.session?.token ?? null;
  }

  async register(name: string, password: string): Promise<void> {
    const r = await api<{ token: string; user: { name: string } }>('/api/register', { method: 'POST', body: { name, password } });
    this.session = { token: r.token, name: r.user.name };
    saveSession(this.session);
  }

  async login(name: string, password: string): Promise<void> {
    const r = await api<{ token: string; user: { name: string } }>('/api/login', { method: 'POST', body: { name, password } });
    this.session = { token: r.token, name: r.user.name };
    saveSession(this.session);
  }

  async logout(): Promise<void> {
    const t = this.token;
    this.session = null;
    saveSession(null);
    if (t) await api('/api/logout', { method: 'POST', token: t }).catch(() => undefined);
  }

  /** Drop the session if the server no longer knows it. */
  async verify(): Promise<void> {
    if (!this.token) return;
    try {
      await api('/api/me', { token: this.token });
    } catch (e) {
      if (String(e).includes('Not signed in')) {
        this.session = null;
        saveSession(null);
      }
    }
  }

  stats(): Promise<UserStats> {
    return api<UserStats>('/api/me/stats', { token: this.token });
  }

  async postPractice(results: RaceResult[]): Promise<void> {
    if (!this.token) return;
    await api('/api/practice-result', { method: 'POST', body: { results }, token: this.token }).catch(() => undefined);
  }

  async postLesson(lessonId: string, wpm: number, accuracy: number, stars: number): Promise<void> {
    if (!this.token) return;
    await api('/api/lesson-result', { method: 'POST', body: { lessonId, wpm, accuracy, stars }, token: this.token }).catch(() => undefined);
  }
}
